import ResponsiveImage from "../components/common/ResponsiveImage";
import React, { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

function copy(text) {
  if (navigator.clipboard) navigator.clipboard.writeText(text);
}

function consumeInviteToken() {
  // Invite tokens deliberately live in the fragment: browsers do not send it
  // to the server or include it in Referer headers. Remove it before rendering
  // so copying the address after loading cannot disclose the token either.
  const fragment = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : "";
  const token = new URLSearchParams(fragment).get("token") || "";

  if (window.location.hash) {
    window.history.replaceState(
      window.history.state,
      document.title,
      `${window.location.pathname}${window.location.search}`,
    );
  }

  return token;
}

export default function InviteAccept() {
  const { user, acceptInvite, completeMfa } = useAuth();
  const [token] = useState(consumeInviteToken); const nav = useNavigate();
  const [password, setPassword] = useState(""); const [confirmPassword, setConfirmPassword] = useState("");
  const [challenge, setChallenge] = useState(null); const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]); const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const isValid = useMemo(() => token && password.length >= 12 && password === confirmPassword, [token, password, confirmPassword]);
  // MFA verification establishes a valid session, but recovery codes must be
  // displayed before redirecting to the app.
  if (user && recoveryCodes.length === 0) return <Navigate to="/" replace />;

  const activate = async (event) => {
    event.preventDefault(); if (!isValid) return;
    setLoading(true); setError("");
    const result = await acceptInvite(token, password);
    setLoading(false);
    if (!result.ok) return setError(result.error);
    setPassword(""); setConfirmPassword("");
    if (result.mfaRequired) setChallenge(result);
    else setRecoveryCodes(result.recoveryCodes || []);
  };

  const verifyEnrollment = async (event) => {
    event.preventDefault();
    setLoading(true); setError("");
    const result = await completeMfa(challenge.challengeId, code.trim());
    setLoading(false);
    if (!result.ok) return setError(result.error);
    setCode("");
    // Enrollment credentials are no longer needed after successful proof.
    setChallenge(null);
    setRecoveryCodes(result.recoveryCodes || []);
  };

  const continueToApp = () => {
    // Recovery codes are intentionally not retained after this one view.
    setRecoveryCodes([]);
    nav("/", { replace: true });
  };

  const enrollment = challenge && <form onSubmit={verifyEnrollment} className="space-y-4" data-testid="invite-mfa-enrollment">
    <div><h2 className="font-semibold">Set up your authenticator</h2><p className="mt-1 text-sm text-muted-foreground">Add this account to an authenticator app, then enter its current six-digit code to activate finance access.</p></div>
    <div className="rounded border border-border bg-muted/30 p-3 space-y-2"><div className="flex justify-center rounded bg-white p-3"><QRCodeSVG value={challenge.enrollmentUri} size={192} level="M" includeMargin /></div><Label className="text-xs">Setup secret</Label><div className="flex gap-2"><code className="min-w-0 flex-1 break-all text-xs">{challenge.enrollmentSecret}</code><Button type="button" size="sm" variant="outline" onClick={() => copy(challenge.enrollmentSecret)} aria-label="Copy setup secret"><Copy className="w-3.5 h-3.5" /></Button></div><Label className="text-xs block pt-2">Authenticator URI</Label><div className="flex gap-2"><code className="min-w-0 flex-1 break-all text-xs">{challenge.enrollmentUri}</code><Button type="button" size="sm" variant="outline" onClick={() => copy(challenge.enrollmentUri)} aria-label="Copy authenticator URI"><Copy className="w-3.5 h-3.5" /></Button></div></div>
    <div><Label htmlFor="enrollment-code" className="text-xs">Authenticator code</Label><Input id="enrollment-code" value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" autoComplete="one-time-code" maxLength={8} placeholder="6-digit code" required data-testid="invite-mfa-code-input" /></div>
    <Button type="submit" className="w-full" disabled={loading || !code}>{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : "Verify and activate"}</Button>
  </form>;

  const recovery = recoveryCodes.length > 0 && <div className="space-y-4" data-testid="recovery-codes-once"><div><h2 className="font-semibold">Save your recovery codes</h2><p className="mt-1 text-sm text-muted-foreground">These codes are shown once. Store them in a secure password manager; each code can be used only once.</p></div><div className="grid grid-cols-2 gap-2 rounded border border-border p-3">{recoveryCodes.map((value) => <code key={value} className="rounded bg-muted px-2 py-1 text-xs">{value}</code>)}</div><Button type="button" className="w-full" onClick={continueToApp}>I saved my codes</Button></div>;

  return <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12" data-testid="invite-accept-page"><div className="w-full max-w-sm space-y-5"><div className="text-center"><ResponsiveImage imageWidth={960} src="/voocly-logo.png" alt="Voocly" className="w-44 mx-auto" /><h1 className="mt-4 text-lg font-semibold">Set up your admin account</h1></div>{recovery || enrollment || <form onSubmit={activate} className="space-y-4"><p className="text-sm text-muted-foreground">Choose a strong password to activate your invitation.</p>{!token && <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">This invitation link is missing its token. Ask a Super Admin to resend it.</div>}<div><Label className="text-xs">New password</Label><Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} required /></div><div><Label className="text-xs">Confirm password</Label><Input type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></div><Button type="submit" className="w-full" disabled={!isValid || loading}>{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Activating…</> : "Activate account"}</Button></form>}{error && <div className="rounded border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">{error}</div>}</div></div>;
}
